import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageSquare, Sparkles, ArrowRight, Lightbulb, Users, Target, User } from "lucide-react";
import { ContactModal } from "../components/ContactModal";

export default function LandingPage() {
  const navigate = useNavigate();
  const [showContact, setShowContact] = useState(false);

  const features = [
    {
      icon: Lightbulb,
      title: "Instant Answers",
      description: "Get quick recommendations for resources, funding, and programs tailored to your needs"
    },
    {
      icon: MessageSquare,
      title: "Ask Anything",
      description: "Use plain English to ask about accelerators, grants, manufacturing support, and more"
    },
    {
      icon: Target,
      title: "Personalized Guidance",
      description: "Receive recommendations based on your startup stage, industry, and specific requirements"
    }
  ];

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#c9a962] rounded-md flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#1e3a5f]" />
            </div>
            <span className="font-bold text-xl text-[#f5f0e6]">ConnectMakeLaunch AI</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost"
              onClick={() => navigate('/about')}
              className="text-[#f5f0e6] hover:bg-[#c9a962]/20 font-medium"
              data-testid="nav-about-btn"
            >
              About
            </Button>
            <Button 
              variant="ghost"
              onClick={() => setShowContact(true)}
              className="text-[#f5f0e6] hover:bg-[#c9a962]/20 font-medium"
              data-testid="nav-contact-btn"
            >
              Contact Us
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-2 border-[#c9a962] text-[#f5f0e6] hover:bg-[#c9a962] hover:text-[#1e3a5f] font-semibold"
              data-testid="login-btn"
            >
              Login / Sign Up
            </Button>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-[#c9a962] hover:bg-[#b8994f] text-[#1e3a5f] font-medium"
              data-testid="start-chatting-btn"
            >
              Start Chatting
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Simplified */}
      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        {/* Platform Description */}
        <div className="space-y-6 mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-[#f5f0e6] rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            AI-Powered Resource Discovery
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1e3a5f] tracking-tight leading-tight">
            Find the right resources<br />
            <span className="text-[#c9a962]">for your company in Southwestern PA</span>
          </h1>
          
          <p className="text-lg text-[#1e3a5f]/70 max-w-2xl mx-auto leading-relaxed">
            ConnectMakeLaunch AI helps entrepreneurs, small businesses, and corporations discover funding, accelerators, manufacturing support, and programs in the Pittsburgh region. 
            Just ask a question and get personalized recommendations instantly.
          </p>
        </div>

        {/* Two Option Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Login/Sign Up Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#1e3a5f] hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-[#1e3a5f] rounded-xl flex items-center justify-center mx-auto mb-4">
              <User className="w-7 h-7 text-[#c9a962]" />
            </div>
            <h3 className="text-xl font-bold text-[#1e3a5f] mb-2">Create an Account</h3>
            <p className="text-[#1e3a5f]/60 text-sm mb-6">
              Get the full experience with saved recommendations and chat history
            </p>
            
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="w-full bg-[#1e3a5f] hover:bg-[#2a4d6e] text-white font-semibold h-12 mb-4"
              data-testid="hero-login-btn"
            >
              Login / Sign Up
            </Button>
            
            <ul className="text-left text-sm text-[#1e3a5f]/70 space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-green-600 font-bold">✓</span> Save & bookmark recommendations
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600 font-bold">✓</span> Chat history across sessions
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600 font-bold">✓</span> Personalized experience
              </li>
            </ul>
          </div>

          {/* Continue as Guest Card */}
          <div className="bg-[#f5f0e8] rounded-2xl shadow-lg p-8 border-2 border-transparent hover:border-[#c9a962] hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-[#c9a962] rounded-xl flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-7 h-7 text-[#1e3a5f]" />
            </div>
            <h3 className="text-xl font-bold text-[#1e3a5f] mb-2">Continue as Guest</h3>
            <p className="text-[#1e3a5f]/60 text-sm mb-6">
              Start chatting right away without creating an account
            </p>
            
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="w-full border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white font-semibold h-12 mb-4"
              data-testid="hero-guest-btn"
            >
              Start Chatting
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <ul className="text-left text-sm text-[#1e3a5f]/50 space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-gray-400">–</span> Full chat access
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gray-400">–</span> No bookmarks or history
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gray-400">–</span> Can sign up later anytime
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">
              How it works
            </h2>
            <p className="text-lg text-[#1e3a5f]/60 max-w-2xl mx-auto">
              Get the information you need in seconds
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-[#f5f0e6] p-8 rounded-xl border border-[#e8e0d0] hover:border-[#1e3a5f] transition-colors duration-200"
              >
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-[#c9a962]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#1e3a5f]/60 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#f5f0e6]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-6">
            Ready to find your resources?
          </h2>
          <p className="text-lg text-[#1e3a5f]/60 mb-8">
            No sign-up required. Just start asking questions.
          </p>
          
          <Button 
            size="lg"
            onClick={() => navigate('/dashboard')}
            className="bg-[#1e3a5f] hover:bg-[#2a4d6e] text-[#f5f0e6] font-medium h-14 px-10 text-lg"
            data-testid="cta-start-btn"
          >
            Start Chatting Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#e8e0d0] py-8 bg-[#1e3a5f]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#c9a962] rounded-md flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#1e3a5f]" />
            </div>
            <span className="font-semibold text-[#f5f0e6]">ConnectMakeLaunch AI</span>
          </div>
          <p className="text-sm text-[#f5f0e6]/60">
            Powered by AI
          </p>
          <button
            onClick={() => navigate('/about')}
            className="text-sm text-[#c9a962] hover:text-[#f5f0e6] font-medium transition-colors"
            data-testid="footer-about-btn"
          >
            About Us
          </button>
          <button
            onClick={() => setShowContact(true)}
            className="text-sm text-[#c9a962] hover:text-[#f5f0e6] font-medium transition-colors"
            data-testid="footer-contact-btn"
          >
            Contact Us
          </button>
        </div>
      </footer>
      <ContactModal open={showContact} onClose={() => setShowContact(false)} mode="contact" />
    </div>
  );
}
